import { createSelector } from 'reselect';

import { Ref } from 'src/sections/repository/types';
import { BucketState } from 'src/types/state';
import createReducer from 'src/utils/create-reducer';

import {
  FetchDiff,
  FetchFileTree,
  FetchSource,
  FilterFiles,
  FILTER_MODE_CHANGE,
  LoadFileTree,
  LoadSourceObject,
  UNLOAD_DIFF,
  UNLOAD_SOURCE,
  CODE_SEARCH_CHANGE,
} from '../actions';
import { FilterMode } from '../constants';
import { FileTree, FilteredFiles, SourceObject } from '../types';
import fileTreeId from '../utils/file-tree-id';

export type SourceSectionState = {
  atRef?: Ref;
  diff?: string;
  filterMode: FilterMode;
  filteredFiles: FilteredFiles | null | undefined;
  filterQuery: string;
  hash?: string;
  isLoading: boolean;
  ref?: Ref;
  sourceHash?: string;
  fileTree: FileTree | null | undefined;
  sourceObject: SourceObject | null | undefined;
  maxItemsDisplayed: number;
};

export const initialState: SourceSectionState = {
  isLoading: false,
  filterMode: FilterMode.Files,
  filteredFiles: null,
  filterQuery: '',
  fileTree: null,
  sourceObject: null,
  maxItemsDisplayed: 500,
};

export const getSectionState = (state: BucketState) =>
  state.repository.source.section;

export const getFileTree = createSelector(
  getSectionState,
  section => section.fileTree
);
export const getFilteredFiles = createSelector(
  getSectionState,
  section => section.filteredFiles
);
export const getFilterQuery = createSelector(
  getSectionState,
  section => section.filterQuery
);
export const getFilterMode = createSelector(
  getSectionState,
  section => section.filterMode
);

export const getSourceDirectoryContents = createSelector(
  getSectionState,
  section =>
    section.sourceObject?.treeEntry?.type === 'directory'
      ? section.sourceObject.treeEntry.contents
      : []
);

export default createReducer(initialState, {
  [FetchDiff.REQUEST](state) {
    return {
      ...state,
      isLoading: true,
    };
  },

  [FetchDiff.SUCCESS](state, action) {
    return {
      ...state,
      ...action.payload,
      isLoading: false,
    };
  },

  [FetchSource.REQUEST](state) {
    return {
      ...state,
      isLoading: true,
    };
  },

  [FetchSource.SUCCESS](state, action) {
    return {
      ...state,
      ...action.payload,
      isLoading: false,
    };
  },

  [FILTER_MODE_CHANGE](state, action) {
    return {
      ...state,
      filterMode: action.payload,
    };
  },

  [CODE_SEARCH_CHANGE](state, action) {
    return {
      ...state,
      filterQuery: action.payload,
    };
  },

  [FilterFiles.REQUEST](state, action) {
    return {
      ...state,
      filterQuery: action.payload,
    };
  },

  [FilterFiles.SUCCESS](state, action) {
    return {
      ...state,
      filteredFiles: action.payload,
      maxItemsDisplayed: 500,
    };
  },

  [FilterFiles.ERROR](state, action) {
    return {
      ...state,
      filteredFiles: action.payload,
      maxItemsDisplayed: 1000,
    };
  },

  [LoadFileTree.REQUEST](state, action) {
    const { repository, hash } = action.payload;
    if (state.fileTree && state.fileTree.id !== fileTreeId(repository, hash)) {
      return {
        ...state,
        fileTree: null,
      };
    }
    return state;
  },

  [FetchFileTree.SUCCESS](state, action) {
    const { meta, payload } = action;
    return {
      ...state,
      fileTree: {
        id: meta && meta.data.treeId,
        tree: payload[0],
      },
    };
  },

  [FetchFileTree.ERROR](state, action) {
    const { meta } = action;
    return {
      ...state,
      fileTree: {
        id: meta && meta.data.treeId,
        tree: null,
        errorCode: meta && meta.status,
      },
    };
  },

  [LoadSourceObject.REQUEST](state) {
    return {
      ...state,
      sourceObject: {
        ...state.sourceObject,
        isLoading: true,
        errorCode: null,
      },
    };
  },

  [LoadSourceObject.SUCCESS](state, action) {
    return {
      ...state,
      sourceObject: {
        ...action.payload,
        isLoading: false,
        errorCode: null,
      },
    };
  },

  [LoadSourceObject.ERROR](state, action) {
    return {
      ...state,
      sourceObject: {
        ...state.sourceObject,
        ...action.payload,
        isLoading: false,
        treeEntry: null,
      },
    };
  },

  [UNLOAD_DIFF](state) {
    return {
      ...state,
      diff: null,
    };
  },

  [UNLOAD_SOURCE](state) {
    return {
      ...state,
      atRef: undefined,
      hash: undefined,
      ref: undefined,
      fileTree: null,
      sourceObject: null,
    };
  },
});
